import React, { useEffect, useState } from 'react';
import { Paper, Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';

const KIND_NAMES = { card: 'Card', fob: 'Key fob', other: 'Other' };

// The member's own gate PIN and cards on /my-account. The PIN stays hidden
// until they press Show — getMyGateAccess says only that one exists, and the
// code itself is fetched from getMyGatePin on demand, so it is never on screen
// (or in the network tab) just because someone opened their account.
export default function MyGateAccess() {
  const functions = getFunctions();
  const [access, setAccess] = useState(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    httpsCallable(functions, 'getMyGateAccess')({})
      .then(({ data }) => setAccess(data))
      .catch(() => setAccess(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const show = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await httpsCallable(functions, 'getMyGatePin')({});
      if (data?.ok) setPin(data.pin);
      else setError('No PIN was found on your account.');
    } catch (e) {
      setError('Could not load your PIN. Try again in a moment.');
    }
    setBusy(false);
  };

  if (!access) return null;
  const { pin: pinInfo, cards = [], memberStanding } = access;
  const goodStanding = memberStanding === 'active' || memberStanding === 'trialing';
  // Nothing to show anyone who has no PIN, no card and no membership that
  // would give them one — renters and juniors don't need a "no PIN" notice.
  if (!pinInfo && cards.length === 0 && !goodStanding) return null;

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>Gate access</Typography>

      {pinInfo ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: cards.length ? 2 : 0 }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
              {pinInfo.dayPass ? 'Day pass PIN' : 'Your gate PIN'}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1.8rem', fontWeight: 700, letterSpacing: '0.3em', color: '#1E3A5F' }}>
              {pin || '●●●●●●'}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" disabled={busy}
            onClick={() => (pin ? setPin('') : show())}
            sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>
            {busy ? <CircularProgress size={16} /> : pin ? 'Hide' : 'Show'}
          </Button>
          <Box sx={{ flexBasis: '100%' }}>
            <Typography variant="body2" sx={{ color: '#555' }}>
              Works: {pinInfo.windowText || 'Any day'}
            </Typography>
            {pinInfo.shared && (
              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                This PIN is shared with your household.
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
              Type it on the gate keypad and press #. Please don't share it outside your household.
            </Typography>
            {error && <Typography variant="caption" sx={{ color: '#E74C3C', display: 'block' }}>{error}</Typography>}
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: '#888', mb: cards.length ? 2 : 0 }}>
          You don't have a gate PIN yet. Ask at the front desk and we'll set one up.
        </Typography>
      )}

      {cards.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Cards and fobs</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {cards.map((c, i) => (
              <Chip key={i} size="small"
                label={`${KIND_NAMES[c.kind] || 'Card'} ${c.hint || ''}`.trim()}
                sx={{ backgroundColor: '#DEEAF1', color: '#1E3A5F', fontWeight: 600 }} />
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
            Tap it on the keypad to open the gate. Lost one? Tell the front desk and we'll switch it off.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
